/**
 * Adattatore dalle fixture del portale statico ai tipi `/api/v2/`.
 * ---------------------------------------------------------------------------
 * File DESTINATO A SPARIRE quando il backend è raggiungibile. Tutto l'attrito
 * fra la forma vecchia dei dati (`dist/comuni.json`, schema KB Arianna) e il
 * contratto v2 sta qui, così `endpoints.ts` e i componenti non lo vedono mai.
 *
 * Non aggiunge, non completa, non inventa: se un campo manca nella fixture,
 * arriva `null` o `[]` ai componenti, che mostrano "non disponibile".
 *
 * ⚠️ Differenze note fra fixture e API reale — sono limiti della fixture, non
 * del contratto:
 *   · i POI del dataset vecchio **non hanno coordinate** (l'API sì): sulla
 *     mappa compare il solo centro del comune;
 *   · le voci non hanno `id` numerico: se ne genera uno posizionale, stabile
 *     solo all'interno della stessa risposta;
 *   · `copertura` non esiste nel dataset vecchio: viene **calcolata qui**
 *     contando gli array. Con l'API reale arriva dal backend e non va
 *     ricalcolata.
 */

import { fixtureGet, paginatedDa } from './client';
import type {
  Attribuzione,
  CategoriaItinerario,
  ComuneDettaglio,
  ComuneSintesi,
  Copertura,
  Evento,
  FaccettaCategoriaTappa,
  Itinerario,
  ItinerarioDettaglio,
  Paginated,
  PaginaRedazionale,
  ProfiloPercorrenza,
  PuntoInteresse,
  SiglaProvincia,
  TappaPubblica,
  TipoEvento,
  VoceComune,
} from './types';

interface LegacyComuniFile {
  metadata: unknown;
  comuni: readonly Record<string, unknown>[];
}

interface LegacyEventiFile {
  metadata?: unknown;
  collezione?: readonly Record<string, unknown>[];
  eventi?: readonly Record<string, unknown>[];
}

let comuniCache: Promise<LegacyComuniFile> | null = null;
function loadComuni(signal?: AbortSignal): Promise<LegacyComuniFile> {
  comuniCache ??= fixtureGet<LegacyComuniFile>('comuni.json', signal);
  return comuniCache;
}

let eventiCache: Promise<LegacyEventiFile> | null = null;
function loadEventi(signal?: AbortSignal): Promise<LegacyEventiFile> {
  eventiCache ??= fixtureGet<LegacyEventiFile>('eventi.json', signal);
  return eventiCache;
}

/**
 * `dev-data/itinerari.json` **nella forma del contratto §I**, non più nella
 * forma del portale statico.
 *
 * Il file legacy (`dist/itinerari.json`) descriveva un modello che non esiste
 * più: le sue `tappe` erano comuni, e nel modello nuovo una tappa punta a un
 * luogo censito. Riproiettarlo avrebbe voluto dire inventare i luoghi
 * (vincolo 4), quindi `npm run fixtures:seed` non lo copia più: scrive una
 * cattura dell'API v2 già nella forma nuova. Un file assente, o rimasto nella
 * forma vecchia, si legge come collezione VUOTA — che è anche lo stato reale
 * della base dati dopo l'archiviazione dei 15 legacy (§I.9).
 */
interface FixtureItinerariFile {
  metadata?: unknown;
  categorie?: readonly Record<string, unknown>[];
  itinerari?: readonly Record<string, unknown>[];
}

let itinerariCache: Promise<FixtureItinerariFile> | null = null;
function loadItinerari(signal?: AbortSignal): Promise<FixtureItinerariFile> {
  itinerariCache ??= fixtureGet<FixtureItinerariFile>('itinerari.json', signal).catch(
    () => ({}) as FixtureItinerariFile,
  );
  return itinerariCache;
}

interface FixturePagineFile {
  metadata?: unknown;
  pagine?: readonly Record<string, unknown>[];
}

let pagineCache: Promise<FixturePagineFile> | null = null;
function loadPagine(signal?: AbortSignal): Promise<FixturePagineFile> {
  pagineCache ??= fixtureGet<FixturePagineFile>('pagine.json', signal);
  return pagineCache;
}

/** Solo per i test: azzera la memoizzazione. */
export function resetFixtureCache(): void {
  comuniCache = null;
  eventiCache = null;
  itinerariCache = null;
  pagineCache = null;
}

function asArray<T>(value: unknown): readonly T[] {
  return Array.isArray(value) ? (value as readonly T[]) : [];
}

function asStringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function asNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Nel dataset vecchio `provincia` è il NOME esteso; in v2 è la SIGLA. */
function toSintesi(raw: Record<string, unknown>): ComuneSintesi {
  return {
    slug: String(raw.slug),
    nome: String(raw.nome),
    // v2: `provincia` = sigla, `provincia_nome` = nome esteso. Il dataset
    // vecchio li ha invertiti, quindi qui si scambiano.
    provincia: raw.provincia_sigla as SiglaProvincia,
    provincia_nome: String(raw.provincia ?? ''),
    cap: asStringOrUndefined(raw.cap),
    popolazione: asNumberOrNull(raw.popolazione),
    coordinate: (raw.coordinate as ComuneSintesi['coordinate']) ?? null,
    attivo: raw.attivo === true,
    piano: (raw.piano as ComuneSintesi['piano']) ?? 'base',
  };
}

/**
 * Porta una voce del dataset vecchio nella forma di `VoceComune`:
 *  · `id` posizionale — il dataset non ne ha, e serve come chiave di riga.
 *    Con l'API reale l'`id` arriva dal backend ed è stabile.
 *  · `fonte` — obbligatoria nel contratto v2, assente nel dataset vecchio.
 *
 * Il cast finale è deliberato e circoscritto: le fixture non sono validate
 * contro lo schema, e questo è l'unico punto in cui il dato grezzo diventa
 * tipato. È anche il motivo per cui questo file è destinato a sparire.
 */
function normalizza<T extends VoceComune>(value: unknown, fonte: string): readonly T[] {
  return asArray<Record<string, unknown>>(value).map((riga, indice) => ({
    ...riga,
    id: indice + 1,
    fonte: String(riga.fonte ?? fonte),
  })) as unknown as readonly T[];
}

/**
 * `copertura` calcolata contando gli array — **solo per le fixture**.
 * Con l'API reale è un campo obbligatorio di `ComuneDettaglio` e non va
 * ricalcolato dal frontend.
 */
function coperturaDa(raw: Record<string, unknown>): Copertura {
  const sezioni = [
    'servizi',
    'scuole',
    'biblioteche',
    'sanita',
    'sociale',
    'trasporti',
    'strutture_ricettive',
    'punti_interesse',
  ];
  const copertura: Record<string, boolean> = {};
  for (const sezione of sezioni) {
    copertura[sezione] = asArray(raw[sezione]).length > 0;
  }
  copertura['identita_narrativa'] = raw.identita_narrativa !== null;
  return copertura;
}

function toDettaglio(raw: Record<string, unknown>): ComuneDettaglio {
  const contattiRaw = (raw.contatti ?? {}) as Record<string, unknown>;
  const narrativa = raw.identita_narrativa as
    | { testo?: string; status?: string; lingua?: string }
    | null;
  const coordinate = raw.coordinate as { lat?: number; lon?: number } | null;

  return {
    ...toSintesi(raw),
    altitudine_m: asNumberOrNull(raw.altitudine_m),
    superficie_kmq:
      raw.superficie_kmq === null || raw.superficie_kmq === undefined
        ? null
        : String(raw.superficie_kmq),
    sindaco: asStringOrUndefined(raw.sindaco),
    data_aggiornamento: asStringOrUndefined(raw.data_aggiornamento) ?? null,
    versione_schema: asStringOrUndefined(raw.version),

    contatti: {
      centralino: asStringOrUndefined(contattiRaw.centralino),
      urp_telefono: asStringOrUndefined(contattiRaw.urp_telefono),
      email: asStringOrUndefined(contattiRaw.email) ?? null,
      pec: asStringOrUndefined(contattiRaw.pec),
      sito_ufficiale: asStringOrUndefined(contattiRaw.sito_ufficiale) ?? null,
      indirizzo: asStringOrUndefined(contattiRaw.indirizzo),
    },

    identita_narrativa: narrativa?.testo
      ? {
          testo: narrativa.testo,
          ...(narrativa.status ? { stato: narrativa.status } : {}),
          ...(narrativa.lingua ? { lingua: narrativa.lingua } : {}),
        }
      : null,

    // Il dataset vecchio non ha preferenze cartografiche: si ricava il centro
    // dalle coordinate del comune, che è l'unico dato realmente presente.
    mappa:
      coordinate && typeof coordinate.lat === 'number' && typeof coordinate.lon === 'number'
        ? {
            centro_lat: String(coordinate.lat),
            centro_lon: String(coordinate.lon),
            /* Voce 45a: il dump vecchio non dichiara il centro abitato —
               null = l'hero ripiega sul baricentro, dichiarandolo. */
            centro_abitato_lat: null,
            centro_abitato_lon: null,
            zoom_default: 13,
            layer_default: 'cartocdn',
          }
        : null,

    overview: null,
    // Gli estratti Wikipedia stanno in un file separato: non vengono uniti qui
    // per non far credere che il contratto li porti dentro il comune.
    wikipedia: null,

    servizi: normalizza(raw.servizi, 'kb-arianna'),
    scuole: normalizza(raw.scuole, 'kb-arianna'),
    biblioteche: normalizza(raw.biblioteche, 'kb-arianna'),
    sanita: normalizza(raw.sanita, 'kb-arianna'),
    sociale: normalizza(raw.sociale, 'kb-arianna'),
    trasporti: normalizza(raw.trasporti, 'kb-arianna'),
    strutture_ricettive: normalizza(raw.strutture_ricettive, 'kb-arianna'),
    // I POI del dataset vecchio NON hanno latitudine/longitudine (l'API sì):
    // restano senza. Non si geocodificano gli indirizzi per riempire il buco.
    punti_interesse: normalizza<PuntoInteresse>(raw.punti_interesse, 'kb-arianna'),

    copertura: coperturaDa(raw),
  };
}

export async function fixtureListComuni(
  signal?: AbortSignal,
): Promise<Paginated<ComuneSintesi>> {
  const file = await loadComuni(signal);
  return paginatedDa(file.comuni.map(toSintesi));
}

export async function fixtureGetComune(
  slug: string,
  signal?: AbortSignal,
): Promise<ComuneDettaglio | null> {
  const file = await loadComuni(signal);
  const raw = file.comuni.find((c) => c.slug === slug);
  return raw ? toDettaglio(raw) : null;
}

/**
 * Nel dataset eventi `provincia` è il NOME esteso ("Bari") e `comune` è lo
 * SLUG. Il contratto v2 vuole la sigla e il nome del comune: il join passa da
 * `comuni.json`, che ha entrambi. Il fallback per nome è per gli eventi il cui
 * comune non risolvesse (oggi: nessuno).
 */
const SIGLA_DA_NOME: Readonly<Record<string, SiglaProvincia>> = {
  Bari: 'BA',
  'Barletta-Andria-Trani': 'BAT',
  Brindisi: 'BR',
  Foggia: 'FG',
  Lecce: 'LE',
  Taranto: 'TA',
};

async function indiceComuni(
  signal?: AbortSignal,
): Promise<ReadonlyMap<string, { nome: string; sigla: SiglaProvincia }>> {
  const file = await loadComuni(signal);
  return new Map(
    file.comuni.map((c) => [
      String(c.slug),
      { nome: String(c.nome), sigla: c.provincia_sigla as SiglaProvincia },
    ]),
  );
}

function toEvento(
  raw: Record<string, unknown>,
  comuni: ReadonlyMap<string, { nome: string; sigla: SiglaProvincia }>,
): Evento {
  const slugComune = String(raw.comune_slug ?? raw.comune ?? '');
  const comune = comuni.get(slugComune);
  const ricorrenza = asStringOrUndefined(raw.data_ricorrente);
  return {
    slug: String(raw.slug ?? raw.id ?? ''),
    titolo: String(raw.titolo ?? raw.nome ?? ''),
    comune: slugComune,
    comune_nome: comune?.nome ?? String(raw.comune_nome ?? slugComune),
    provincia:
      comune?.sigla ??
      SIGLA_DA_NOME[String(raw.provincia ?? '')] ??
      (raw.provincia_sigla as SiglaProvincia),
    coordinate: (raw.coordinate as Evento['coordinate']) ?? null,
    /* `quando` con `certezza` esplicita. Il dataset ha `starts_at`/`ends_at`
       (null su 362 eventi su 362) e `data_ricorrente` ("23 aprile"): quella è
       una ricorrenza DICHIARATA dalla fonte, e si mostra come testo. Dove
       manca anche quella, la certezza è ignota — non si fabbrica una data. */
    quando: {
      certezza: ricorrenza ? 'ricorrente' : 'ignota',
      inizio: asStringOrUndefined(raw.starts_at ?? raw.data_inizio ?? raw.data) ?? null,
      fine: asStringOrUndefined(raw.ends_at ?? raw.data_fine) ?? null,
      testo: ricorrenza ?? null,
    },
    fonte: String(raw.fonte ?? 'dataset-eventi'),
    organizzatore: null,
    ...(asStringOrUndefined(raw.tipo) ? { tipo: raw.tipo as TipoEvento } : {}),
    ...(asStringOrUndefined(raw.descrizione) ? { descrizione: String(raw.descrizione) } : {}),
    ...(asStringOrUndefined(raw.venue) ? { venue: String(raw.venue) } : {}),
    ...(asStringOrUndefined(raw.indirizzo) ? { indirizzo: String(raw.indirizzo) } : {}),
    ...(asStringOrUndefined(raw.lingua) ? { lingua: String(raw.lingua) } : {}),
    ...(asStringOrUndefined(raw.url_ufficiale)
      ? { url_ufficiale: String(raw.url_ufficiale) }
      : {}),
    ...(asStringOrUndefined(raw.cover_url) ? { cover_url: String(raw.cover_url) } : {}),
    ...(asStringOrUndefined(raw.status) ? { stato: raw.status as Evento['stato'] } : {}),
    ...(asStringOrUndefined(raw.qualita) ? { qualita: String(raw.qualita) } : {}),
    ...(Array.isArray(raw.categoria_tag) ? { tag: raw.categoria_tag } : {}),
  };
}

export async function fixtureListEventi(signal?: AbortSignal): Promise<Paginated<Evento>> {
  const [file, comuni] = await Promise.all([loadEventi(signal), indiceComuni(signal)]);
  const righe = file.collezione ?? file.eventi ?? [];
  return paginatedDa(righe.map((raw) => toEvento(raw, comuni)));
}

export async function fixtureGetEvento(
  slug: string,
  signal?: AbortSignal,
): Promise<Evento | null> {
  const pagina = await fixtureListEventi(signal);
  return pagina.risultati.find((e) => e.slug === slug) ?? null;
}

// ---------------------------------------------------------------------------
// Itinerari, tappe e categorie — contratto §I
// ---------------------------------------------------------------------------
//
// Qui NON c'è più adattamento di forma: la fixture è già una cattura della
// risposta v2. L'unico lavoro è distinguere l'elenco dal dettaglio (le
// fixture tengono un file solo) e riconoscere la forma vecchia per non
// servirla come se fosse quella nuova.

/** Una riga della fixture è nella forma nuova? La vecchia aveva `gruppo`. */
function formaNuova(raw: Record<string, unknown>): boolean {
  return !('gruppo' in raw) && !('durata_giorni' in raw);
}

const DURATA_VUOTA: Itinerario['durata'] = {
  valore: null,
  unita: '',
  origine: 'calcolata',
  calcolata: { minuti: null, valore: null, unita: '', completa: true },
  dichiarata: null,
  scarto_minuti: null,
};

/** I campi dell'ELENCO, cioè il dettaglio meno sequenza, profilo e faccette. */
function toItinerario(raw: Record<string, unknown>): Itinerario {
  return {
    slug: String(raw.slug ?? ''),
    titolo: String(raw.titolo ?? ''),
    categoria: (raw.categoria as Itinerario['categoria']) ?? null,
    tono: (raw.tono as Itinerario['tono']) ?? 'istituzionale',
    durata: (raw.durata as Itinerario['durata']) ?? DURATA_VUOTA,
    tappe_totali: asNumberOrNull(raw.tappe_totali) ?? 0,
    comuni: asArray<string>(raw.comuni),
    fonte: typeof raw.fonte === 'string' ? raw.fonte : null,
    ...(asStringOrUndefined(raw.sottotitolo) ? { sottotitolo: String(raw.sottotitolo) } : {}),
    ...(asStringOrUndefined(raw.testo) ? { testo: String(raw.testo) } : {}),
    ...(asStringOrUndefined(raw.testo_semplice)
      ? { testo_semplice: String(raw.testo_semplice) }
      : {}),
    ...(asStringOrUndefined(raw.icona) ? { icona: String(raw.icona) } : {}),
    ...(Array.isArray(raw.tag) ? { tag: raw.tag } : {}),
    lunghezza_km: asNumberOrNull(raw.lunghezza_km),
    superficie_kmq: asNumberOrNull(raw.superficie_kmq),
    ...(asStringOrUndefined(raw.stagione_consigliata)
      ? { stagione_consigliata: String(raw.stagione_consigliata) }
      : {}),
    ...(asStringOrUndefined(raw.fonte_url) ? { fonte_url: String(raw.fonte_url) } : {}),
    ...(asStringOrUndefined(raw.fonte_dichiarata)
      ? { fonte_dichiarata: String(raw.fonte_dichiarata) }
      : {}),
  };
}

/**
 * Le buste `{meta, risultato}` catturate. Una riga nella forma vecchia — o
 * senza busta — non si serve: mostrarla come se fosse quella nuova
 * significherebbe far leggere campi che non esistono più.
 */
async function busteItinerari(
  signal?: AbortSignal,
): Promise<readonly { meta: Record<string, unknown>; risultato: Record<string, unknown> }[]> {
  const file = await loadItinerari(signal);
  const buste: { meta: Record<string, unknown>; risultato: Record<string, unknown> }[] = [];
  for (const voce of file.itinerari ?? []) {
    const risultato = (voce.risultato ?? voce) as Record<string, unknown>;
    if (!formaNuova(risultato)) continue;
    buste.push({ meta: (voce.meta as Record<string, unknown>) ?? {}, risultato });
  }
  return buste;
}

export async function fixtureListItinerari(
  filtri: { readonly categoria?: string } = {},
  signal?: AbortSignal,
): Promise<Paginated<Itinerario>> {
  const buste = await busteItinerari(signal);
  let risultati = buste.map((b) => toItinerario(b.risultato));
  if (filtri.categoria) {
    risultati = risultati.filter((i) => i.categoria?.slug === filtri.categoria);
  }
  return paginatedDa(risultati);
}

export async function fixtureGetItinerario(
  slug: string,
  signal?: AbortSignal,
): Promise<{
  readonly itinerario: ItinerarioDettaglio;
  readonly attribuzione: readonly Attribuzione[];
} | null> {
  const buste = await busteItinerari(signal);
  const busta = buste.find((b) => String(b.risultato.slug ?? '') === slug);
  if (!busta) return null;
  const raw = busta.risultato;
  const itinerario: ItinerarioDettaglio = {
    ...toItinerario(raw),
    tappe: asArray<TappaPubblica>(raw.tappe),
    profilo: (raw.profilo as ProfiloPercorrenza | undefined) ?? {
      tappe: 0,
      distanza_totale_km: null,
      distanza_completa: true,
      tratte_senza_distanza: 0,
      tempo_trasferimenti_minuti: null,
      tempo_soste_minuti: null,
      tempo_totale_minuti: null,
      tempo_completo: true,
      tratte_senza_tempo: 0,
      tappe_senza_sosta: 0,
      lunghezza_dichiarata_km: null,
    },
    categorie_tappe: asArray<FaccettaCategoriaTappa>(raw.categorie_tappe),
  };
  return {
    itinerario,
    attribuzione: asArray<Attribuzione>(
      (busta.meta as { attribuzione?: unknown }).attribuzione,
    ),
  };
}

export async function fixtureListCategorieItinerario(
  signal?: AbortSignal,
): Promise<readonly CategoriaItinerario[]> {
  const file = await loadItinerari(signal);
  return asArray<CategoriaItinerario>(file.categorie);
}

export async function fixtureGetCategoriaItinerario(
  slug: string,
  signal?: AbortSignal,
): Promise<CategoriaItinerario | null> {
  const categorie = await fixtureListCategorieItinerario(signal);
  return categorie.find((c) => c.slug === slug) ?? null;
}

// ---------------------------------------------------------------------------
// Pagine redazionali — `dev-data/pagine.json`, export dal DB del backend
// ---------------------------------------------------------------------------

/**
 * ⚠️ La fixture contiene le BOZZE e qui vengono servite come se fossero
 * pubblicate. È deliberato e vale SOLO in sviluppo: in dev non c'è una
 * redazione che pubblica, e una fixture che rispettasse lo stato mostrerebbe
 * 19 pagine 404. L'API reale serve solo le pubblicate (decisione 42):
 * il comportamento di produzione è quello, e i test del backend lo coprono.
 */
export async function fixtureGetPagina(
  slug: string,
  lingua: string,
  signal?: AbortSignal,
): Promise<PaginaRedazionale | null> {
  const file = await loadPagine(signal);
  const righe = (file.pagine ?? []).filter((p) => p.slug === slug);
  if (righe.length === 0) return null;
  // Fallback di lingua identico al backend: richiesta → italiano.
  const riga = righe.find((p) => p.lingua === lingua) ?? righe.find((p) => p.lingua === 'it');
  if (!riga) return null;
  return {
    slug: String(riga.slug),
    area: riga.area as PaginaRedazionale['area'],
    lingua: String(riga.lingua),
    tradotta: riga.lingua === lingua,
    titolo: String(riga.titolo ?? ''),
    sottotitolo: String(riga.sottotitolo ?? ''),
    // Voce 48: il dump vecchio non ha l'occhiello — vuoto = non si rende.
    occhiello: String(riga.occhiello ?? ''),
    blocchi: (riga.blocchi ?? []) as PaginaRedazionale['blocchi'],
    decorrenza: (riga.decorrenza as string | null) ?? null,
    versione: 0, // le bozze non hanno versioni pubblicate
    pubblicata_il: null,
  };
}
